import { useState, useEffect, useRef, useCallback } from 'react';
import { Gift, Plus, Trash2, Loader2, Search, X, Folder, Package, ChevronDown } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import Dropdown from './Dropdown';
import { api } from '../../lib/spizirnaUi';

const BARVA = '#22C55E';

const TYPY = [
    { value: 'nabidka', label: 'Nabídka — host si combo přidá' },
    { value: 'auto', label: 'Auto — samo v košíku' },
];

/**
 * Jedna combo skupina — název, co nabízí a za kolik (2026-10-22).
 *
 * Otevírá se odkudkoli, kde se s comby pracuje: „Přidat combo" na kategorii
 * či produktu (nová skupina se rovnou přiřadí), tužka na kartě comba
 * a přehled „Combo skupiny" v seznamu produktů. Cena položky platí jen
 * uvnitř comba a pro všechna místa, kde je skupina přiřazená — proto to
 * editor u ceny připomíná počtem míst.
 *
 * `skupinaId` null = nová skupina; po založení se editor přepne na úpravu,
 * ať jde rovnou přidávat položky. `onZavrit(skupina)` dostane uloženou
 * skupinu (nebo null, když se nic nezaložilo).
 */
export default function ComboSkupinaEditor({ skupinaId = null, isDark, onZavrit }) {
    const [id, setId] = useState(skupinaId);
    const [form, setForm] = useState({ nazev: '', popis: '', typ_zobrazeni: 'nabidka', aktivni: true });
    const [mist, setMist] = useState(0);
    const [polozky, setPolozky] = useState([]);
    const [prirazeni, setPrirazeni] = useState(null); // { mista, produkty }
    const [dph, setDph] = useState([]);
    const [nacitam, setNacitam] = useState(!!skupinaId);
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState('');

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const pole = { background: isDark ? 'rgba(255,255,255,0.06)' : '#fff', border: `1px solid ${border}`, color: 'var(--text-primary)', borderRadius: 8, padding: '8px 10px', fontSize: 13, width: '100%' };

    const nactiPolozky = useCallback(async (sid) => {
        try { setPolozky(await api(`/api/recepty/combo-skupiny/${sid}/polozky`)); } catch { setPolozky([]); }
    }, []);

    // Kde je combo nastavené — načte se, jakmile skupina existuje.
    useEffect(() => {
        if (!id) return;
        api(`/api/recepty/combo-skupiny/${id}/prirazeni`).then(setPrirazeni).catch(() => setPrirazeni({ mista: [], produkty: [] }));
    }, [id]);

    useEffect(() => {
        api('/api/ciselniky/dph-sazby').then(d => setDph((d || []).filter(x => x.aktivni !== false))).catch(() => {});
        if (!skupinaId) return;
        (async () => {
            try {
                const vse = await api('/api/recepty/combo-skupiny');
                const s = (vse || []).find(x => x.id === skupinaId);
                if (s) {
                    setForm({ nazev: s.nazev, popis: s.popis || '', typ_zobrazeni: s.typ_zobrazeni, aktivni: !!s.aktivni, poradi: s.poradi });
                    setMist(s.mist_count || 0);
                }
                await nactiPolozky(skupinaId);
            } catch (e) { setChyba(e?.error || 'Skupinu se nepodařilo načíst'); }
            setNacitam(false);
        })();
    }, [skupinaId, nactiPolozky]);

    const ulozitSkupinu = async (zmena = {}) => {
        const novy = { ...form, ...zmena };
        if (!novy.nazev.trim()) return;
        setUkladam(true);
        try {
            const data = { ...novy, nazev: novy.nazev.trim(), poradi: novy.poradi ?? 0 };
            const s = id
                ? await api(`/api/recepty/combo-skupiny/${id}`, 'PUT', data)
                : await api('/api/recepty/combo-skupiny', 'POST', data);
            if (!id) setId(s.id);
            setChyba('');
        } catch (e) { setChyba(e?.error || e?.message || 'Skupinu se nepodařilo uložit'); }
        setUkladam(false);
    };

    const zmenitPolozku = async (p, data) => {
        try { await api(`/api/recepty/combo-polozky/${p.id}`, 'PUT', data); await nactiPolozky(id); }
        catch (e) { setChyba(e?.error || 'Položku se nepodařilo uložit'); }
    };
    const odebratPolozku = async (p) => {
        if (!confirm(`Odebrat ${p.recept?.nazev || 'položku'} z comba?`)) return;
        try { await api(`/api/recepty/combo-polozky/${p.id}`, 'DELETE'); await nactiPolozky(id); }
        catch (e) { setChyba(e?.error || 'Položku se nepodařilo odebrat'); }
    };

    const zavrit = () => onZavrit?.(id ? { id, nazev: form.nazev } : null);
    const dphMoznosti = dph.map(d => ({ value: d.id, label: `${Number(d.sazba)} %` }));

    return (
        <ModalShell onClose={zavrit} isDark={isDark} ikona={Gift} barva={BARVA} sirka={620}
            nadpis={id ? `Combo: ${form.nazev || ''}` : 'Nová combo skupina'}
            paticka={
                <div className="flex items-center justify-between gap-2 w-full">
                    <span className="text-[11px]" style={{ color: muted }}>
                        {id && mist > 0 ? `Přiřazeno na ${mist} ${mist === 1 ? 'místě' : 'místech'} — změna platí všude.` : ''}
                    </span>
                    <button type="button" onClick={zavrit} className="text-xs font-semibold px-4 py-2 rounded-lg"
                        style={{ background: id ? BARVA : 'none', color: id ? '#fff' : muted, border: id ? 'none' : `1px solid ${border}`, cursor: 'pointer' }}>
                        {id ? 'Hotovo' : 'Zrušit'}
                    </button>
                </div>
            }>
            {nacitam ? (
                <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin" style={{ color: muted }} /></div>
            ) : (
                <div className="p-4 space-y-4">
                    {chyba && <p className="text-[11px]" style={{ color: '#EF4444' }}>{chyba}</p>}

                    {/* Hlavička skupiny — ukládá se při opuštění pole, nová tlačítkem. */}
                    <div className="space-y-2">
                        <div className="flex gap-2">
                            <input value={form.nazev} placeholder="Název comba (např. Přílohy k burgeru)" autoFocus={!id}
                                onChange={e => setForm(f => ({ ...f, nazev: e.target.value }))}
                                onBlur={() => { if (id) ulozitSkupinu(); }}
                                onKeyDown={e => { if (e.key === 'Enter') { if (id) e.currentTarget.blur(); else ulozitSkupinu(); } }}
                                style={pole} />
                        </div>
                        <input value={form.popis} placeholder="Popis pro zákazníka (nepovinné)"
                            onChange={e => setForm(f => ({ ...f, popis: e.target.value }))}
                            onBlur={() => { if (id) ulozitSkupinu(); }}
                            style={pole} />
                        <div className="flex items-center gap-3 flex-wrap">
                            <div style={{ minWidth: 260 }}>
                                <Dropdown value={form.typ_zobrazeni} isDark={isDark} accent={BARVA} height={34} options={TYPY}
                                    onChange={v => { setForm(f => ({ ...f, typ_zobrazeni: v })); if (id) ulozitSkupinu({ typ_zobrazeni: v }); }} />
                            </div>
                            <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={form.aktivni} onChange={e => { const v = e.target.checked; setForm(f => ({ ...f, aktivni: v })); if (id) ulozitSkupinu({ aktivni: v }); }}
                                    style={{ accentColor: BARVA, width: 15, height: 15 }} />
                                Aktivní
                            </label>
                            {ukladam && <Loader2 size={13} className="animate-spin ml-auto" style={{ color: muted }} />}
                        </div>
                        {!id && (
                            <button type="button" onClick={() => ulozitSkupinu()} disabled={ukladam || !form.nazev.trim()}
                                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg"
                                style={{ background: BARVA, color: '#fff', border: 'none', cursor: form.nazev.trim() ? 'pointer' : 'not-allowed', opacity: form.nazev.trim() ? 1 : 0.5 }}>
                                {ukladam ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Založit a přidat položky
                            </button>
                        )}
                    </div>

                    {id && prirazeni && <KdeSeNabizi prirazeni={prirazeni} muted={muted} border={border} />}

                    {id && (
                        <div className="space-y-2">
                            <h4 className="text-[11px] font-semibold uppercase" style={{ color: muted, letterSpacing: '0.05em' }}>Co combo nabízí</h4>
                            {polozky.length === 0 && (
                                <p className="text-xs py-2" style={{ color: muted }}>Zatím nic — přidej produkt a jeho cenu v combu.</p>
                            )}
                            {polozky.map(p => (
                                <RadekPolozky key={p.id} p={p} dph={dphMoznosti} isDark={isDark} muted={muted} border={border}
                                    onZmena={(data) => zmenitPolozku(p, data)} onOdebrat={() => odebratPolozku(p)} />
                            ))}
                            <PridatPolozku skupinaId={id} dph={dphMoznosti} isDark={isDark} muted={muted} border={border} pole={pole}
                                poradi={polozky.length} vSkupine={polozky.map(p => p.recept_id)}
                                onPridano={() => nactiPolozky(id)} onChyba={setChyba} />
                        </div>
                    )}
                </div>
            )}
        </ModalShell>
    );
}

function RadekPolozky({ p, dph, isDark, muted, border, onZmena, onOdebrat }) {
    const cena = Number(p.cena_combo);
    const bezna = p.cena_regularni != null ? Number(p.cena_regularni) : null;
    return (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ border: `1px solid ${border}` }}>
            <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {p.recept?.nazev}{p.recept?.varianta ? ` (${p.recept.varianta})` : ''}
                </div>
                <div className="text-[10px]" style={{ color: muted }}>
                    {bezna != null ? `běžně ${Math.round(bezna)} Kč` : 'bez běžné ceny'}
                    {bezna && bezna > cena ? ` · host ušetří ${Math.round(bezna - cena)} Kč` : ''}
                </div>
            </div>
            <label className="flex items-center gap-1 text-[11px]" style={{ color: muted }}>
                <input type="number" min="0" step="1" defaultValue={cena} key={`${p.id}-${cena}`}
                    onBlur={e => { const v = parseFloat(e.target.value); if (!Number.isNaN(v) && v !== cena) onZmena({ cena_combo: v }); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    className="rounded text-right"
                    style={{ width: 64, padding: '4px 6px', fontSize: 12, fontWeight: 600, color: BARVA, background: isDark ? 'rgba(255,255,255,0.06)' : '#fff', border: `1px solid ${border}` }} />
                Kč
            </label>
            <div style={{ width: 86 }}>
                <Dropdown value={p.dph_sazba_id} options={dph} isDark={isDark} accent={BARVA} height={30}
                    onChange={v => onZmena({ dph_sazba_id: v })} />
            </div>
            <button type="button" onClick={onOdebrat} title="Odebrat z comba"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}>
                <Trash2 size={14} />
            </button>
        </div>
    );
}

function PridatPolozku({ skupinaId, dph, isDark, muted, border, pole, poradi, vSkupine, onPridano, onChyba }) {
    const [hledat, setHledat] = useState('');
    const [vysledky, setVysledky] = useState([]);
    const [vybrany, setVybrany] = useState(null);
    const [cena, setCena] = useState('');
    const [sazba, setSazba] = useState(null);
    const [ukladam, setUkladam] = useState(false);
    const casovac = useRef(null);

    useEffect(() => { if (sazba == null && dph.length) setSazba(dph.find(d => d.label.startsWith('12'))?.value ?? dph[0].value); }, [dph, sazba]);

    const hledej = (q) => {
        setHledat(q);
        clearTimeout(casovac.current);
        if (q.trim().length < 2) { setVysledky([]); return; }
        casovac.current = setTimeout(async () => {
            try {
                const r = await api(`/api/recepty/recepty?typ=PRO&search=${encodeURIComponent(q.trim())}`);
                const seznam = Array.isArray(r) ? r : (r?.data || []);
                // Jen prodávané produkty; archiv do comba nepatří.
                setVysledky(seznam.filter(x => !vSkupine.includes(x.id) && x.aktivni !== false).slice(0, 12));
            } catch { setVysledky([]); }
        }, 250);
    };

    const pridat = async () => {
        if (!vybrany || cena === '' || !sazba) return;
        setUkladam(true);
        try {
            await api(`/api/recepty/combo-skupiny/${skupinaId}/polozky`, 'POST', {
                recept_id: vybrany.id, cena_combo: parseFloat(cena), dph_sazba_id: sazba, poradi, aktivni: true,
            });
            setVybrany(null); setHledat(''); setCena(''); setVysledky([]);
            onPridano();
        } catch (e) { onChyba(e?.error || 'Položku se nepodařilo přidat'); }
        setUkladam(false);
    };

    return (
        <div className="rounded-lg p-3 space-y-2" style={{ border: `1px dashed ${BARVA}66`, background: `${BARVA}0a` }}>
            {!vybrany ? (
                <div className="relative">
                    <Search size={13} style={{ position: 'absolute', left: 10, top: 11, color: muted }} />
                    <input value={hledat} onChange={e => hledej(e.target.value)} placeholder="Přidat produkt do comba — hledej název…"
                        style={{ ...pole, paddingLeft: 30 }} />
                    {vysledky.length > 0 && (
                        <div className="mt-1 rounded-lg overflow-hidden max-h-52 overflow-y-auto" style={{ border: `1px solid ${border}`, background: isDark ? 'rgba(24,22,32,0.98)' : '#fff' }}>
                            {vysledky.map(r => (
                                <button key={r.id} type="button" onClick={() => { setVybrany(r); setVysledky([]); }}
                                    className="w-full text-left px-3 py-2 text-xs"
                                    style={{ background: 'none', border: 'none', borderBottom: `1px solid ${border}`, cursor: 'pointer', color: 'var(--text-primary)' }}>
                                    {r.nazev}{r.varianta ? ` (${r.varianta})` : ''}
                                    {/* Kategorie a kód — dva stejně pojmenované produkty
                                        (Zakysaná smetana 2×) jinak nejdou rozlišit. */}
                                    <span className="block text-[10px]" style={{ color: muted }}>
                                        {[r.kategorie?.nazev, r.kod].filter(Boolean).join(' · ')}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md" style={{ background: `${BARVA}1f`, color: 'var(--text-primary)' }}>
                        {vybrany.nazev}{vybrany.varianta ? ` (${vybrany.varianta})` : ''}
                        <button type="button" onClick={() => setVybrany(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: 0, display: 'inline-flex' }}><X size={12} /></button>
                    </span>
                    <label className="flex items-center gap-1 text-[11px]" style={{ color: muted }}>
                        cena v combu
                        <input type="number" min="0" step="1" value={cena} autoFocus onChange={e => setCena(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') pridat(); }}
                            className="rounded text-right"
                            style={{ width: 70, padding: '4px 6px', fontSize: 12, background: isDark ? 'rgba(255,255,255,0.06)' : '#fff', border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                        Kč
                    </label>
                    <div style={{ width: 86 }}>
                        <Dropdown value={sazba} options={dph} isDark={isDark} accent={BARVA} height={30} onChange={setSazba} />
                    </div>
                    <button type="button" onClick={pridat} disabled={ukladam || cena === ''}
                        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg ml-auto"
                        style={{ background: BARVA, color: '#fff', border: 'none', cursor: cena === '' ? 'not-allowed' : 'pointer', opacity: cena === '' ? 0.5 : 1 }}>
                        {ukladam ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Přidat
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * Kde je combo nastavené (kategorie, produkty — i kde je vypnuté) a kolika
 * produktům se ve výsledku nabízí. Přiřazuje se v záložce Combo kategorie
 * či produktu; tady je to jen vidět, ať je jasné, co změna ceny zasáhne.
 */
function KdeSeNabizi({ prirazeni, muted, border }) {
    const [rozbaleno, setRozbaleno] = useState(false);
    const prirazena = prirazeni.mista.filter(m => m.prirazena);
    const vypnuta = prirazeni.mista.filter(m => !m.prirazena);
    const produkty = prirazeni.produkty || [];

    const Stitek = ({ m, vypnuto }) => {
        const Ikona = m.typ === 'kategorie' ? Folder : Package;
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium"
                title={m.typ === 'kategorie' ? 'Kategorie — dědí podkategorie a produkty pod ní' : 'Produkt'}
                style={vypnuto
                    ? { background: 'rgba(239,68,68,0.10)', color: '#EF4444', textDecoration: 'line-through' }
                    : { background: `${BARVA}1a`, color: 'var(--text-primary)' }}>
                <Ikona size={11} style={{ color: vypnuto ? '#EF4444' : BARVA, flexShrink: 0 }} />
                {m.nazev}
            </span>
        );
    };

    return (
        <div className="rounded-lg p-3 space-y-2" style={{ border: `1px solid ${border}` }}>
            <h4 className="text-[11px] font-semibold uppercase" style={{ color: muted, letterSpacing: '0.05em' }}>Kde se nabízí</h4>
            {prirazena.length === 0 ? (
                <p className="text-xs" style={{ color: muted }}>
                    Zatím nikde. Přiřadíš ho v záložce Combo u kategorie nebo produktu.
                </p>
            ) : (
                <div className="flex flex-wrap gap-1.5">{prirazena.map(m => <Stitek key={`${m.typ}:${m.id}`} m={m} />)}</div>
            )}
            {vypnuta.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px]" style={{ color: muted }}>vypnuté u:</span>
                    {vypnuta.map(m => <Stitek key={`${m.typ}:${m.id}`} m={m} vypnuto />)}
                </div>
            )}
            {produkty.length > 0 && (
                <div>
                    <button type="button" onClick={() => setRozbaleno(r => !r)}
                        className="flex items-center gap-1 text-[11px] font-medium"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: 0 }}>
                        <ChevronDown size={12} style={{ transform: rozbaleno ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                        Ve výsledku u {produkty.length} {produkty.length === 1 ? 'produktu' : 'produktů'} (i zděděně)
                    </button>
                    {rozbaleno && (
                        <div className="mt-1.5 text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                            {produkty.map(p => p.nazev).join(' · ')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
